import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, getSelectedTenant, setSelectedTenant } from '../api/client';
import type { TenantPage, TenantSummary } from '../api/types';
import { useApi, useMutation } from '../lib/use-api';
import { formatDateTime, formatNumber } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { TenantDialog } from '../components/TenantDialog';

export function TenantsPage({ onTenantSwitched }: { onTenantSwitched?: () => void }): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [editing, setEditing] = useState<TenantSummary | 'new' | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | undefined>(undefined);

  const currentSelectedTenant = getSelectedTenant();
  const tenants = useApi<TenantPage>('/api/admin/tenants');

  const rotateKey = useMutation<TenantSummary, { kid: string; algorithm: string }>(
    useCallback(
      (tenant: TenantSummary) =>
        apiFetch<{ kid: string; algorithm: string }>(
          `/api/admin/lti/keys/rotate?tenantId=${encodeURIComponent(tenant.id)}`,
          { method: 'POST', body: {} },
        ),
      [],
    ),
  );

  const confirmRotate = async (tenant: TenantSummary): Promise<void> => {
    if (!window.confirm(t('tenants.rotateConfirm', { name: tenant.displayName }))) return;
    const result = await rotateKey.run(tenant);
    if (result !== undefined) {
      setSuccessMsg(
        t('tenants.rotateSuccess', { name: tenant.displayName, kid: result.kid }),
      );
      tenants.reload();
    }
  };

  const handleSwitchTenant = (tenantId: string | undefined): void => {
    setSelectedTenant(tenantId);
    if (onTenantSwitched) {
      onTenantSwitched();
    } else {
      window.location.reload();
    }
  };

  const filteredItems = (tenants.data?.items ?? []).filter((item) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return (
      item.displayName.toLowerCase().includes(q) ||
      item.id.toLowerCase().includes(q) ||
      item.slug.toLowerCase().includes(q)
    );
  });

  return (
    <section>
      <h1>{t('tenants.heading')}</h1>
      <p className="hint">{t('tenants.intro')}</p>

      {successMsg ? (
        <div
          className="feedback feedback--success"
          style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>{successMsg}</span>
          <button
            type="button"
            className="button--text"
            style={{ padding: '0 4px' }}
            onClick={() => setSuccessMsg(undefined)}
          >
            ✕
          </button>
        </div>
      ) : null}

      <div className="toolbar" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setEditing('new')}>
          {t('tenants.addTenant')}
        </button>

        <input
          type="search"
          placeholder={t('tenants.searchPlaceholder')}
          value={search}
          style={{ flex: '1 1 240px', maxWidth: '400px' }}
          onChange={(e) => setSearch(e.target.value)}
        />

        {currentSelectedTenant ? (
          <button
            type="button"
            className="button--secondary"
            onClick={() => handleSwitchTenant(undefined)}
          >
            {t('tenants.switchAll')}
          </button>
        ) : null}
      </div>

      {tenants.loading ? <Loading /> : null}
      {tenants.error ? <ErrorNotice error={tenants.error} onRetry={tenants.reload} /> : null}
      {rotateKey.error ? <ErrorNotice error={rotateKey.error} /> : null}

      {tenants.data && !tenants.loading ? (
        filteredItems.length === 0 ? (
          <EmptyNotice />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('tenants.column.displayName')}</th>
                <th scope="col">{t('tenants.column.id')}</th>
                <th scope="col">{t('tenants.column.slug')}</th>
                <th scope="col">{t('tenants.column.stats')}</th>
                <th scope="col">{t('tenants.column.ltiKey')}</th>
                <th scope="col">{t('tenants.column.status')}</th>
                <th scope="col">{t('tenants.column.createdAt')}</th>
                <th scope="col">{t('library.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const isCurrent = currentSelectedTenant === item.id;
                return (
                  <tr
                    key={item.id}
                    style={isCurrent ? { backgroundColor: 'var(--color-surface-selected, #f0fdf4)' } : {}}
                  >
                    <td>
                      <strong>{item.displayName}</strong>
                      {isCurrent ? (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                            backgroundColor: '#22c55e',
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        >
                          ✓ Hiện tại
                        </span>
                      ) : null}
                    </td>
                    <td className="mono">{item.id}</td>
                    <td className="mono">{item.slug}</td>
                    <td>
                      <span title="Học liệu">{formatNumber(item.contentCount, locale)} HL</span>
                      {' / '}
                      <span title="Người dùng">{formatNumber(item.userCount, locale)} ND</span>
                      {item.platformCount > 0 ? (
                        <>
                          {' / '}
                          <span title="Nền tảng LTI">{item.platformCount} LMS</span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {item.activeKid ? (
                        <span
                          className="mono"
                          title={`Active Key ID: ${item.activeKid}`}
                          style={{ fontSize: '0.8rem' }}
                        >
                          🔑 {item.activeKid.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="muted">{t('tenants.noKey')}</span>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          backgroundColor:
                            item.status === 'active'
                              ? 'var(--badge-active-bg, #dcfce7)'
                              : 'var(--badge-suspended-bg, #fee2e2)',
                          color:
                            item.status === 'active'
                              ? 'var(--badge-active-fg, #166534)'
                              : 'var(--badge-suspended-fg, #991b1b)',
                        }}
                      >
                        {t(`tenants.status.${item.status}`)}
                      </span>
                    </td>
                    <td>{formatDateTime(item.createdAt, locale)}</td>
                    <td className="actions">
                      <button type="button" onClick={() => setEditing(item)}>
                        {t('common:action.edit')}
                      </button>

                      <button
                        type="button"
                        onClick={() => void confirmRotate(item)}
                        disabled={rotateKey.pending}
                        title={t('tenants.rotateKey')}
                      >
                        {t('tenants.rotateKey')}
                      </button>

                      {!isCurrent ? (
                        <button
                          type="button"
                          className="button--secondary"
                          onClick={() => handleSwitchTenant(item.id)}
                          title={t('tenants.switchToTenant')}
                        >
                          {t('tenants.manageThis')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="button--text"
                          onClick={() => handleSwitchTenant(undefined)}
                        >
                          Bỏ chọn
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      ) : null}

      {editing ? (
        <TenantDialog
          tenant={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            tenants.reload();
          }}
        />
      ) : null}
    </section>
  );
}
